import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { Guid } from "guid-typescript";
import { makeStyles } from "helpers/StyleHelper";
import { useMemo } from "react";
import { FieldInputProps, FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IOrganizationDetails,
  IOrganizationSaveRequest,
  OrganizationSaveRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import OrganizationField from "./OrganizationField";
import SelectOrganizationType from "components/SelectOrganizationType";
import SelectOrganizationStatus from "components/SelectOrganizationStatus";
import { OrganizationStatus, OrganizationType } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  parentOrganizationLabel: {
    color: "black",
    fontWeight: "bold",
    fontStyle: "italic",
  },
}));

type OrganizationFormValues = IOrganizationSaveRequest;

const { Form } = withTypes<OrganizationFormValues>();

type OrganizationFormProps = {
  organization?: IOrganizationDetails;
  submitLabel: string;
  onSubmit: (
    values: IOrganizationSaveRequest
  ) => object | Promise<object | void> | void;
  onCancel?: () => void;
};

const OrganizationForm = ({
  organization,
  submitLabel,
  onCancel,
  onSubmit,
}: OrganizationFormProps &
  Omit<FormProps<OrganizationFormValues>, "onSubmit">) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const isEdit = !!organization;

  const { organizationId } = contextStore.useState();

  const init = useMemo<OrganizationFormValues>(
    () => ({
      name: organization?.name ?? "",
      description: organization?.description,
      displayName: organization?.displayName,
      accountId: organization?.accountId,
      organizationTypeId:
        organization?.organizationTypeId ?? OrganizationType.Client,
      organizationStatusId:
        organization?.organizationStatusId ?? OrganizationStatus.Active,
    }),
    [organization]
  );

  const validateForm = (values: OrganizationFormValues) => {
    const errors = {} as any;

    if (values.displayName && values.displayName?.length > 255) {
      errors.displayName = t({ id: "inputValueTooLongLabel" });
    }

    if (values.accountId && !Guid.isGuid(values.accountId)) {
      errors.accountId = t({ id: "invalidGuid" });
    }

    return errors;
  };

  const setOrganizationStatusIdHandler = (
    organizationTypeId: string | undefined,
    input: FieldInputProps<any, HTMLElement>,
    change: (field: "organizationStatusId", value?: string) => void
  ) => {
    input.onChange(organizationTypeId);

    if (
      organizationTypeId !== undefined &&
      organizationTypeId !== OrganizationType.Client
    ) {
      change("organizationStatusId", OrganizationStatus.Active);
    }
  };

  return (
    <>
      <Form
        onSubmit={async (values) => {
          const transformed = OrganizationSaveRequest.fromJS(values);

          return await onSubmit(transformed);
        }}
        validate={validateForm}
        initialValues={init}
        render={({
          form: { change },
          handleSubmit,
          submitting,
          submitError,
          values,
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="name" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "nameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                  <Field name="displayName" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FieldRichText
                          input={input}
                          meta={meta}
                          label={t({ id: "displayNameLabel" })}
                          init={{
                            statusbar: false,
                          }}
                          oneLineMode="true"
                        />
                      </Grid>
                    )}
                  </Field>
                  <Field name="description">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "descriptionLabel" })}
                          InputLabelProps={{ required: false }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="accountId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "accountIdLabel" })}
                          InputLabelProps={{ required: false }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="organizationTypeId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectOrganizationType
                            {...input}
                            label={t({ id: "organizationTypeLabel" })}
                            disabled={isEdit}
                            textFieldProps={{
                              required: true,
                              error: meta.error && meta.submitFailed,
                            }}
                            onChange={(organizationTypeId) =>
                              setOrganizationStatusIdHandler(
                                organizationTypeId,
                                input,
                                change
                              )
                            }
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="organizationStatusId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectOrganizationStatus
                            {...input}
                            label={t({ id: "organizationStatusLabel" })}
                            disabled={
                              values.organizationTypeId !==
                              OrganizationType.Client
                            }
                            textFieldProps={{
                              required: true,
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="parentOrganization">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <OrganizationField
                            {...input}
                            organizationId={
                              isEdit ? organization?.parentId : organizationId
                            }
                            helperText={
                              meta.error && meta.submitFailed && meta.error
                            }
                            label={t({ id: "parentOrganizationLabel" })}
                            tooltipEnabled={!isEdit}
                            tooltipHeader={t({
                              id: "parentOrganizationHeaderTooltip",
                            })}
                            tooltipBody={t({
                              id: "parentOrganizationBodyTooltip",
                            })}
                            error={meta.error && meta.submitFailed}
                            disabled={isEdit}
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitting}
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </>
  );
};

export default OrganizationForm;
